import {
  Autocomplete,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { getOwnCompanyProducts } from 'lib/api/company';
import { getDecisionEngines } from 'lib/api/decisionEngine';
import { getAvailableBusinessForFund } from 'lib/api/multiverse/fund';
import { createEvaluation } from 'lib/api/multiverse/fund';
import type { AvailableBusiness } from 'lib/api/multiverse/fund/types';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { showAlert } from 'lib/functions/utils';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { TType } from '..';

type Props = {
  type: TType;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
};

const ExternalEvaluation: React.FC<Props> = ({ type, setOpen, onSuccess }) => {
  const dispatch = useDispatch();
  const [selectedRelation, setSelectedRelation] = useState<AvailableBusiness>();
  const [selectedProduct, setSelectedProduct] = useState<number>();
  const [selectedEngine, setSelectedEngine] = useState<number>();
  const [selectedCompany, setSelectedCompany] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const { mutate, isPending } = createEvaluation(dispatch);
  const { data: relations, isPending: isPendingRelations } =
    getAvailableBusinessForFund(dispatch);
  const { data: products, isPending: isPendingProducts } =
    getOwnCompanyProducts(dispatch);
  const { data: engines, isPending: isPendingEngines } = getDecisionEngines(
    dispatch,
    type,
  );

  const handleSubmit = async () => {
    if (
      selectedRelation === undefined ||
      selectedProduct === undefined ||
      selectedEngine === undefined ||
      selectedCompany?.id === undefined
    ) {
      showAlert(
        dispatch,
        'error',
        'Por favor, selecciona todos los campos requeridos.',
      );
      return;
    }
    mutate(
      {
        businessIdentifier: selectedRelation.businessIdentifier,
        productId: selectedProduct,
        decisionEngineId: selectedEngine,
        companyId: selectedCompany?.id,
        businessName: selectedRelation.businessName,
      },
      {
        onSuccess: () => {
          setOpen(false);
          onSuccess?.();
          showAlert(
            dispatch,
            'success',
            'Evaluación creada correctamente. Vuelve en unos minutos para revisar los resultados.',
          );
        },
        onError: () => {
          showAlert(
            dispatch,
            'error',
            'Ha habido un error al crear la evaluación',
          );
        },
      },
    );
  };

  const loading = useMemo(() => {
    return isPendingRelations || isPendingProducts || isPendingEngines;
  }, [isPendingRelations, isPendingProducts, isPendingEngines]);

  const disabled = useMemo(() => {
    return (
      selectedRelation === null ||
      selectedProduct === undefined ||
      selectedEngine === undefined ||
      isPending
    );
  }, [selectedRelation, selectedProduct, selectedEngine, isPending]);

  const companies = useMemo(() => {
    const aux = new Map<number, { id: number; name: string }>();
    relations?.forEach((relation) => {
      aux.set(relation.companyId, {
        id: relation.companyId,
        name: relation.companyName,
      });
    });
    return Array.from(aux.values());
  }, [relations]);

  const filteredRelations = useMemo(() => {
    return relations?.filter(
      (relation) => relation.companyId === selectedCompany?.id,
    );
  }, [relations, selectedCompany?.id]);

  return (
    <>
      {loading ? (
        <DialogContent sx={{ minHeight: 300 }}>
          <CenteredCircularProgress type="dialog" />
        </DialogContent>
      ) : (
        <DialogContent>
          <Stack direction="column" spacing={2} pb={4} pt={1}>
            <Autocomplete
              options={companies || []}
              value={selectedCompany}
              getOptionLabel={(option) => `${option.name}`}
              renderInput={(params) => (
                <TextField
                  style={{ minWidth: 250 }}
                  {...params}
                  label="Selecciona la financiera"
                />
              )}
              onChange={(_, newValue) => setSelectedCompany(newValue)}
              fullWidth
            />
            <Autocomplete
              options={filteredRelations || []}
              value={selectedRelation}
              getOptionLabel={(option) =>
                `${option.businessName} - ${option.businessIdentifier}`
              }
              renderInput={(params) => (
                <TextField
                  style={{ minWidth: 250 }}
                  {...params}
                  label="Selecciona el cliente a evaluar"
                />
              )}
              onChange={(_, newValue) =>
                setSelectedRelation(newValue ?? undefined)
              }
              fullWidth
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="product-label">Tipo de revisión</InputLabel>
              <Select
                id="product-select"
                labelId="product-label"
                label="Tipo de revisión"
                value={selectedProduct ?? ''}
                onChange={(e) => setSelectedProduct(Number(e.target.value))}
              >
                {products?.map((product) => (
                  <MenuItem key={product.id} value={product.id}>
                    {product.productType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel id="engine-label">
                Que revisión quieres realizar?
              </InputLabel>
              <Select
                id="engine-select"
                labelId="engine-label"
                label="Que revisión quieres realizar?"
                value={selectedEngine ?? ''}
                onChange={(e) => setSelectedEngine(Number(e.target.value))}
              >
                {engines?.map((engine) => (
                  <MenuItem key={engine.id} value={engine.id}>
                    {engine.name} (
                    {engine.engineType === 'tree'
                      ? 'Árbol de decisión'
                      : 'Matriz de decisión'}
                    )
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <AlertBanner
            severity="info"
            title="Información"
            message="La evaluación se ejecutará en segundo plano. Vuelve en unos minutos para revisar los resultados. Si la información del SII o del TGR está desactualizada, se consultará automáticamente antes de ejecutar la evaluación."
          />
        </DialogContent>
      )}
      <DialogActions>
        <CustomButton color="secondary" onClick={() => setOpen(false)}>
          Cancelar
        </CustomButton>
        <CustomButton
          disabled={disabled}
          loading={isPending}
          color="primary"
          onClick={() => handleSubmit()}
        >
          Evaluar
        </CustomButton>
      </DialogActions>
    </>
  );
};

export default ExternalEvaluation;
